// Lightweight info-tooltip — a small ⓘ icon that reveals a popover on hover/focus.
// Designed for explaining settings sliders, model trade-offs, costs, etc. The
// popover uses `whitespace-pre-line` so callers can pass multi-paragraph copy
// with `\n\n` separators.
//
// iter-26 fixes:
//   1. `pointer-events-auto` on the popover so the cursor can ENTER it without
//      breaking hover — fixes "tooltip disappears the moment I move the mouse".
//   2. Auto-flip placement: if there isn't enough room below/right, the tooltip
//      flips up/left so it never gets clipped by the viewport edge.
//   3. New `placement` prop with values: "bottom-right" (default), "top-right",
//      "top-left", "bottom-left", "bottom-center". Plus a `wide` prop for longer
//      helper copy.
//
// Usage:
//   <InfoTip label="What does this do?" placement="top-right">
//     {longHelpText}
//   </InfoTip>

import { useEffect, useRef, useState } from "react";
import { Info, Eye } from "@phosphor-icons/react";

const ICON_MAP = { info: Info, eye: Eye };

const PLACEMENTS = {
  "bottom-right": "top-full mt-2 left-0",
  "bottom-left":  "top-full mt-2 right-0",
  "bottom-center": "top-full mt-2 left-1/2 -translate-x-1/2",
  "top-right":    "bottom-full mb-2 left-0",
  "top-left":     "bottom-full mb-2 right-0",
  "top-center":   "bottom-full mb-2 left-1/2 -translate-x-1/2",
};

export default function InfoTip({ children, text, label = "More info", placement = "bottom-center", side, width = 320, testid, icon = "info", iconSize = 14, mobileCenter = false }) {
  // May 24, 2026 — back-compat: many callers pass `text="..."` instead of
  // `children`. Accept either; `children` wins if both are given.
  const body = children ?? text;
  // Backwards-compat: callers using the old `side` prop still work.
  if (side && placement === "bottom-center") {
    placement = side === "left" ? "bottom-left" : side === "center" ? "bottom-center" : "bottom-right";
  }
  const [open, setOpen] = useState(false);
  const [resolvedPlacement, setResolvedPlacement] = useState(placement);
  const wrapperRef = useRef(null);
  const tipRef = useRef(null);
  const closeTimerRef = useRef(null);

  // Jul 2026 — `mobileCenter`: on narrow screens the popover can clip off the
  // left/right edge when the icon sits near a viewport border. When opted in,
  // render the popover as a fixed, viewport-centered sheet on mobile so it's
  // always fully visible and centered on the screen.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 639px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync); else mq.addListener(sync);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", sync); else mq.removeListener(sync); };
  }, []);
  const useSheet = mobileCenter && isMobile;

  // iter-35f: grace-period open/close — the previous version defined these
  // helpers but the handlers below never used them, leaving a 4px gap between
  // the icon and the popover that the cursor would cross, instantly closing
  // the tooltip and causing a site-wide flicker. Wiring them in fixes it.
  const scheduleOpen = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => { setOpen(false); closeTimerRef.current = null; }, 180);
  };
  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  // Auto-flip when the chosen placement would clip the viewport.
  // iter-40 — flicker fix: only run the measurement ONCE per open and only
  // *commit* the flipped placement once. Previously the effect re-ran every
  // time `resolvedPlacement` changed, which could ping-pong (top→bottom→top)
  // when the popover sat near a viewport edge and the flipped position itself
  // would also clip — causing visible flicker. Now we measure, decide, set,
  // done. On close we reset back to the caller's preferred placement so the
  // next open starts clean.
  useEffect(() => {
    if (!open) {
      if (resolvedPlacement !== placement) setResolvedPlacement(placement);
      return;
    }
    if (!wrapperRef.current || !tipRef.current) return;
    const tip = tipRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let next = placement;
    if (tip.right > vw - 8) next = next.replace("right", "left").replace("center", "left");
    if (tip.left < 8) next = next.replace("left", "right");
    if (tip.bottom > vh - 8 && next.startsWith("bottom")) next = next.replace("bottom", "top");
    if (tip.top < 8 && next.startsWith("top")) next = next.replace("top", "bottom");
    if (next !== resolvedPlacement) setResolvedPlacement(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement]);

  const anchorClass = PLACEMENTS[resolvedPlacement] || PLACEMENTS["bottom-center"];

  return (
    <span
      ref={wrapperRef}
      className="relative inline-flex align-middle"
      data-testid={testid}
      // iter-35f: now using grace-period handlers so cursor can cross the gap
      // between the icon and the popover without instantly closing.
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={scheduleOpen}
      onBlur={scheduleClose}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        title={label}
        className="text-[#6B6A68] hover:text-[#4A5D4E] focus:text-[#4A5D4E] focus:outline-none cursor-help"
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
      >
        {(() => { const Icon = ICON_MAP[icon] || Info; return <Icon size={iconSize} weight="duotone" />; })()}
      </button>
      {open && (useSheet ? (
        <>
          <span className="fixed inset-0 z-[95]" onClick={() => setOpen(false)} aria-hidden="true" />
          <span
            role="tooltip"
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
            className="pointer-events-auto fixed z-[100] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-sm bg-[#1C1B1A] text-[#FDFCF8] text-[12px] leading-relaxed rounded-lg p-4 shadow-2xl normal-case tracking-normal text-left font-normal whitespace-pre-line"
          >
            {body}
          </span>
        </>
      ) : (
        <span
          ref={tipRef}
          role="tooltip"
          // iter-35f: grace-period handlers also wired on the popover so the
          // cursor entering the popover re-cancels the close timer.
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          className={`pointer-events-auto absolute z-[90] ${anchorClass} bg-[#1C1B1A] text-[#FDFCF8] text-[11px] leading-relaxed rounded-md p-3 shadow-2xl normal-case tracking-normal text-left font-normal whitespace-pre-line`}
          style={{ width: `${width}px`, maxWidth: "min(92vw, 480px)" }}
        >
          {body}
        </span>
      ))}
    </span>
  );
}
